import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, MessageSquare, Reply } from 'lucide-react';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { formatDistanceToNowLocale } from '@/lib/date-locale';
import { VoiceNotePlayer } from './VoiceNotePlayer';
import { MediaPreview } from './MediaPreview';
import { FileAttachment } from './FileAttachment';
import { CallEventBubble } from './CallEventBubble';
import { hapticTap } from '@/lib/native-haptics';
import { ReadReceiptIndicator } from './ReadReceiptIndicator';
import { MessageRetryIndicator } from './MessageRetryIndicator';
import { MessageContextMenu } from './MessageContextMenu';
import { LocationBubble } from './LocationBubble';
import { LinkPreview } from './LinkPreview';
import { EventBubble, type EventData } from './EventBubble';
import { PollBubble, type PollData } from './PollBubble';

export interface UnifiedMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  is_encrypted: boolean;
  message_type: string | null;
  file_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  file_type?: string | null;
  duration_seconds?: number | null;
  thumbnail_url?: string | null;
  reply_to_id?: string | null;
  read_at?: string | null;
  reactions?: Record<string, string[]> | null;
  expires_at?: string | null;
  delivered_at?: string | null;
  source?: 'e2ee' | 'sms' | 'call';
  reply_to_content?: string;
  reply_to_sender?: string;
  /** Per-file decryption key for Signal-grade encrypted media */
  file_key?: string | null;
  edited_at?: string | null;
  is_deleted?: boolean;
}

interface UnifiedMessageBubbleProps {
  message: UnifiedMessage;
  isOwn: boolean;
  onReply?: (message: UnifiedMessage) => void;
  onReact?: (messageId: string, emoji: string) => void;
  onDelete?: (messageId: string) => void;
  onDeleteForMe?: (messageId: string) => void;
  onEdit?: (message: UnifiedMessage) => void;
  onRetry?: (messageId: string) => void;
  onReturnCall?: (userId: string, isVideo: boolean) => void;
  onStar?: (messageId: string) => void;
  isStarred?: boolean;
  currentUserId?: string;
  /** Display name of the other user in this conversation */
  otherUserName?: string;
}

export function UnifiedMessageBubble({
  message,
  isOwn,
  onReply,
  onReact,
  onDelete,
  onDeleteForMe,
  onEdit,
  onRetry,
  onReturnCall,
  onStar,
  isStarred,
  currentUserId,
  otherUserName,
}: UnifiedMessageBubbleProps) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showTapback, setShowTapback] = useState(false);
  const [burstEmoji, setBurstEmoji] = useState<string | null>(null);
  const lastTapRef = useRef(0);
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const type = message.message_type || 'text';

  const tapbackEmojis = ['❤️', '👍', '👎', '😂', '‼️', '❓'];

  const handleDoubleTap = useCallback(() => {
    // Suppress the click that follows a long-press release.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      hapticTap();
      onReact?.(message.id, '❤️');
      setBurstEmoji('❤️');
      setTimeout(() => setBurstEmoji(null), 800);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }, [message.id, onReact]);

  const handleTapbackSelect = useCallback((emoji: string) => {
    hapticTap();
    onReact?.(message.id, emoji);
    setBurstEmoji(emoji);
    setTimeout(() => setBurstEmoji(null), 800);
    setShowTapback(false);
  }, [message.id, onReact]);

  const [isLongPressing, setIsLongPressing] = useState(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const bubbleRef = useRef<HTMLDivElement>(null);

  // Swipe-right (or swipe-left for own messages) → reply
  const swipeHandlers = useSwipeGesture(
    bubbleRef,
    {
      onSwipeRight: !isOwn && onReply ? () => { hapticTap(); onReply(message); } : undefined,
      onSwipeLeft: isOwn && onReply ? () => { hapticTap(); onReply(message); } : undefined,
    },
    { threshold: 60, maxVertical: 24 },
  );

  const cancelLongPress = useCallback(() => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
    setIsLongPressing(false);
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    setIsLongPressing(true);
    longPressFiredRef.current = false;
    longPressRef.current = setTimeout(() => {
      hapticTap();
      longPressFiredRef.current = true;
      setShowContextMenu(true);
      longPressRef.current = null;
      setIsLongPressing(false);
    }, 500);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!longPressRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    if (dx > 10 || dy > 10) {
      cancelLongPress();
    }
  };

  const handlePointerUp = () => {
    cancelLongPress();
  };

  // Call events
  if (type === 'call_event') {
    const metadata = typeof message.content === 'string' ? (() => {
      try { return JSON.parse(message.content); } catch { return {}; }
    })() : {};
    // Determine who to call back: the other party
    const callBackUserId = metadata.caller_id === currentUserId
      ? message.sender_id !== currentUserId ? message.sender_id : undefined
      : metadata.caller_id;
    return (
      <CallEventBubble
        isVideo={metadata.is_video || false}
        duration={metadata.duration || message.duration_seconds || undefined}
        isMissed={metadata.is_missed || false}
        isOwn={isOwn}
        status={metadata.status}
        callerName={otherUserName}
        timestamp={message.created_at}
        onReturnCall={onReturnCall && callBackUserId
          ? (isVideo) => onReturnCall(callBackUserId, isVideo)
          : undefined}
      />
    );
  }

  // System messages
  if (type === 'system') {
    return (
      <div className="flex items-center justify-center py-3">
        <p className="text-[11px] text-muted-foreground/60 bg-muted/40 px-3 py-1 rounded-full">
          {message.content}
        </p>
      </div>
    );
  }

  // Sticker messages (large emoji)
  if (type === 'sticker') {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group relative`}>
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="text-[72px] leading-none py-1"
        >
          {message.content}
        </motion.div>
      </div>
    );
  }

  // Location messages
  if (type === 'location') {
    let locData = { lat: 0, lng: 0, address: '' };
    try { locData = JSON.parse(message.content); } catch {}

    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group relative`}>
        <div className={`px-1 py-1 ${
          isOwn
            ? 'bg-[hsl(200,80%,40%)] text-white rounded-[20px] rounded-br-[6px]'
            : 'bg-[#F0F0F0] dark:bg-[#2A2A2A] text-[#1A1A1A] dark:text-[#F0F0F0] rounded-[20px] rounded-bl-[6px]'
        }`}>
          <LocationBubble lat={locData.lat} lng={locData.lng} address={locData.address} isOwn={isOwn} />
          <div className={`flex items-center gap-1 mt-1 px-2.5 pb-1 ${isOwn ? 'justify-end' : ''}`}>
            <span className={`text-[11px] ${isOwn ? 'text-white/60' : 'text-[#666] dark:text-[#999]'}`}>
              {formatDistanceToNowLocale(new Date(message.created_at), { addSuffix: false })}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Event messages
  if (type === 'event') {
    let eventData: EventData;
    try { eventData = JSON.parse(message.content); } catch { eventData = { title: 'Event', date: new Date().toISOString() }; }
    return <EventBubble data={eventData} isOwn={isOwn} currentUserId={currentUserId} />;
  }

  // Poll messages
  if (type === 'poll') {
    let pollData: PollData;
    try { pollData = JSON.parse(message.content); } catch { pollData = { question: 'Poll', options: [] }; }
    return <PollBubble data={pollData} isOwn={isOwn} currentUserId={currentUserId} />;
  }

  // Deleted messages
  if (message.is_deleted) {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group relative`}>
        <div className={`px-3.5 py-2 rounded-[20px] ${
          isOwn ? 'rounded-br-[6px]' : 'rounded-bl-[6px]'
        } border border-border/30 bg-muted/30`}>
          <p className="text-[13px] text-muted-foreground/60 italic">
            🚫 This message was deleted
          </p>
        </div>
      </div>
    );
  }

  // 3-state delivery receipts: sent → delivered → read (Signal-style)
  const receiptStatus = isOwn
    ? message.read_at
      ? 'read' as const
      : message.delivered_at
        ? 'delivered' as const
        : 'sent' as const
    : undefined;

  // Detect pending/failed messages from offline queue
  const msgType = message.message_type || '';
  const isPending = msgType.includes('|pending');
  const isFailed = msgType.includes('|failed');

  const parsedReactions = message.reactions
    ? Object.entries(message.reactions as Record<string, string[]>).map(([emoji, users]) => ({
        emoji,
        count: users.length,
        hasReacted: users.includes(currentUserId || ''),
      }))
    : [];

  return (
    <div
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group relative ${showContextMenu || showTapback ? 'z-50' : ''}`}
      onContextMenu={(e) => {
        e.preventDefault();
        setShowContextMenu(true);
      }}
      role="article"
      aria-label={`${isOwn ? 'You' : 'Contact'}: ${type === 'text' ? message.content : type}. ${formatDistanceToNowLocale(new Date(message.created_at), { addSuffix: true })}`}
    >
      {/* Reply hint icon (revealed by swipe translateX) */}
      {onReply && (
        <div
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${isOwn ? 'right-0' : 'left-0'} h-8 w-8 flex items-center justify-center rounded-full bg-primary/15 text-primary opacity-0 group-[.swiping]:opacity-100`}
          aria-hidden
        >
          <Reply className="h-4 w-4" />
        </div>
      )}
      <div
        ref={bubbleRef}
        className="relative max-w-[78%] select-none"
        style={{
          transform: isLongPressing ? 'scale(0.97)' : 'scale(1)',
          transition: 'transform 0.15s ease',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'pan-y',
        }}
        onClick={handleDoubleTap}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onTouchStart={swipeHandlers.onTouchStart}
        onTouchMove={swipeHandlers.onTouchMove}
        onTouchEnd={swipeHandlers.onTouchEnd}
      >
        {/* Emoji burst animation */}
        <AnimatePresence>
          {burstEmoji && (
            <motion.div
              initial={{ scale: 0, opacity: 1 }}
              animate={{ scale: 1.4, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
            >
              <span className="text-4xl">{burstEmoji}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* iMessage-style tapback bar */}
        <AnimatePresence>
          {showTapback && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setShowTapback(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.7, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: 8 }}
                transition={{ type: 'spring', stiffness: 500, damping: 28 }}
                className="absolute z-50 bg-card border border-border/40 rounded-full shadow-elevated px-1.5 py-1 flex gap-0.5 backdrop-blur-xl"
                style={{ bottom: '100%', marginBottom: 6, ...(isOwn ? { right: 0 } : { left: 0 }) }}
              >
                {tapbackEmojis.map((emoji) => (
                  <motion.button
                    key={emoji}
                    whileHover={{ scale: 1.25 }}
                    whileTap={{ scale: 0.85 }}
                    onClick={(e) => { e.stopPropagation(); handleTapbackSelect(emoji); }}
                    className="w-9 h-9 flex items-center justify-center text-lg rounded-full hover:bg-muted/60 transition-colors"
                  >
                    {emoji}
                  </motion.button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Reply preview */}
        {message.reply_to_content && (
          <div
            className={`px-3 py-1.5 mb-0.5 rounded-t-2xl text-xs border-l-2 ${
              isOwn ? 'bg-[hsl(200,80%,35%)] text-white/70 border-white/30' : 'bg-muted/70 border-muted-foreground/30'
            }`}
          >
            <p className="font-semibold text-[11px] text-primary/80 mb-0.5">
              {message.reply_to_sender || 'User'}
            </p>
            <p className="truncate leading-tight">{message.reply_to_content}</p>
          </div>
        )}

        {/* Main bubble */}
        <div
          className={`px-3.5 py-2.5 ${
            isOwn
              ? 'bg-[hsl(200,80%,40%)] text-white rounded-[20px] rounded-br-[6px]'
              : 'bg-[#F0F0F0] dark:bg-[#2A2A2A] text-[#1A1A1A] dark:text-[#F0F0F0] rounded-[20px] rounded-bl-[6px]'
          }`}
        >
          {/* Source indicator */}
          {message.source && (
            <div className="flex items-center gap-1 mb-0.5">
              {message.source === 'e2ee' && (
              <Lock className={`h-2.5 w-2.5 ${isOwn ? 'text-white/30' : 'text-muted-foreground/30'}`} />
            )}
            {message.source === 'sms' && (
              <MessageSquare className={`h-2.5 w-2.5 ${isOwn ? 'text-white/30' : 'text-muted-foreground/30'}`} />
              )}
            </div>
          )}

          {/* Content by type */}
          {type === 'text' && message.content === 'Message' && message.is_encrypted && (
            <p className="text-xs text-muted-foreground/60 italic flex items-center gap-1">
              <Lock className="h-3 w-3" />
              This message can't be decrypted on this device
            </p>
          )}
          {type === 'text' && !(message.content === 'Message' && message.is_encrypted) && (
            <>
              <p className="text-base leading-relaxed whitespace-pre-wrap break-words">
                {message.content}
              </p>
              <LinkPreview content={message.content} isOwn={isOwn} />
            </>
          )}

          {(type === 'image' || type === 'video') && message.file_url && (
            <MediaPreview
              url={message.file_url}
              type={type as 'image' | 'video'}
              thumbnailUrl={message.thumbnail_url || undefined}
              isOwn={isOwn}
              fileKey={message.file_key}
              fileType={message.file_type || undefined}
            />
          )}

          {type === 'voice_note' && message.file_url && (
            <VoiceNotePlayer
              url={message.file_url}
              duration={message.duration_seconds || undefined}
              isOwn={isOwn}
              fileKey={message.file_key}
            />
          )}

          {type === 'audio' && message.file_url && (
            <audio src={message.file_url} controls className="max-w-full" preload="auto" aria-label="Audio message" />
          )}

          {type === 'file' && message.file_url && (
            <FileAttachment
              url={message.file_url}
              fileName={message.file_name || 'File'}
              fileSize={message.file_size || 0}
              fileType={message.file_type || ''}
              isOwn={isOwn}
              fileKey={message.file_key}
            />
          )}

          {/* Caption for media */}
          {type !== 'text' && type !== 'voice_note' && type !== 'file' && message.content && message.content !== '{}' && (
            <p className="text-sm mt-1.5 leading-snug">{message.content}</p>
          )}

          {/* Timestamp + edited + read receipt */}
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
            {message.edited_at && (
              <span className={`text-[10px] italic ${isOwn ? 'text-white/40' : 'text-[#999] dark:text-[#777]'}`}>
                edited
              </span>
            )}
            <span className={`text-[11px] ${isOwn ? 'text-white/60' : 'text-[#666] dark:text-[#999]'}`}>
              {formatDistanceToNowLocale(new Date(message.created_at), { addSuffix: false })}
            </span>
            {isOwn && !isPending && !isFailed && receiptStatus && (
              <ReadReceiptIndicator
                status={receiptStatus}
                className={`h-3 w-3 ${isOwn ? 'text-white/60' : ''}`}
              />
            )}
          </div>

          {/* Pending / Failed message indicator */}
          {isOwn && (isPending || isFailed) && (
            <MessageRetryIndicator
              status={isFailed ? 'failed' : 'pending'}
              onRetry={() => onRetry?.(message.id)}
            />
          )}
        </div>

        {/* Sticky tapback badge - Apple style, pinned to bubble corner */}
        {parsedReactions.length > 0 && (
          <div className={`flex ${isOwn ? 'justify-end mr-1' : 'justify-start ml-1'} -mt-2 relative z-10`}>
            <div className="flex items-center gap-0.5 bg-card border border-border/40 rounded-full px-1.5 py-0.5 shadow-sm">
              {parsedReactions.map((r) => (
                <button
                  key={r.emoji}
                  onClick={(e) => { e.stopPropagation(); onReact?.(message.id, r.emoji); }}
                  className={`text-sm ${r.hasReacted ? 'opacity-100' : 'opacity-70'}`}
                >
                  {r.emoji}
                </button>
              ))}
              {parsedReactions.reduce((sum, r) => sum + r.count, 0) > 1 && (
                <span className="text-[10px] text-muted-foreground font-medium ml-0.5">
                  {parsedReactions.reduce((sum, r) => sum + r.count, 0)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Context menu */}
        <MessageContextMenu
          isOpen={showContextMenu}
          onClose={() => setShowContextMenu(false)}
          onReply={() => onReply?.(message)}
          onCopy={() => {}}
          onDelete={() => onDelete?.(message.id)}
          onDeleteForMe={() => onDeleteForMe?.(message.id)}
          onEdit={type === 'text' ? () => onEdit?.(message) : undefined}
          onReact={() => setShowTapback(true)}
          onStar={onStar ? () => onStar(message.id) : undefined}
          isOwn={isOwn}
          isStarred={isStarred}
          messageContent={message.content}
        />
      </div>
    </div>
  );
}
